import client from '../../apolloClient';
import { gql } from 'apollo-boost';
import ContentSectionGroup from './types/ContentSectionGroup';
import Roles from "@/utils/permissions";

const activitySetItem = `
                    id
                    type
                    name
                    slug
                    isOptional
                    shortDescription
                    introduction
                    isBlockBasedIdeProject
                    ideProjectType
                    ideType
                    hasParent
                    ideProjectStarterCode {
                      id
                      title
                      projectDescription
                    }
                    goals
                    estimatedTimeInMinutes
                    tags
                    externalUrl
                    permissionLevel
                    easeOfFacilitation
                    welcomeModal {
                      id
                      name
                      modalContentSections {
                        name
                        heading
                        content
                        buttonLabel
                      }
                    }
                    activities {
                      id
                      name
                      slug
                      estimatedTimeInMinutes
                      tags
                      shortDescription
                      isOptional
                      permissionLevel
                      easeOfFacilitation
                      activityType {
                        name
                        colorTheme
                      }
                      contentSections {
                        id
                        type
                        isIdeProjectExpandedCurriculum
                        ... on ContentSection {
                          heading
                          subheading
                          content
                          facilitatorNote
                          visibleInPageNavigation
                          onlyVisibleToFacilitators
                          isOpenTextAnswer
                          requiredGrading
                          isOptionalTextAnswer
                          isIdeProjectHideBlockPalette
                          contentSectionGroup {
                            ${ContentSectionGroup}
                          }
                        }
                        ... on QuizQuestion {
                          name
                          header
                          description
                          isOptionalQuiz
                          answers {
                            ... on QuizAnswer {
                              name
                              answerTitle
                              answerBody
                              hint
                              isCorrect
                            }
                          }
                        }
                      }
                      icon {
                        name
                        src
                        alt
                      }
                      contentSections {
                        id
                        type
                        isIdeProjectExpandedCurriculum
                        ... on ContentSection {
                          heading
                          subheading
                          content
                          facilitatorNote
                          visibleInPageNavigation
                          onlyVisibleToFacilitators
                          isOpenTextAnswer
                          requiredGrading
                          isOptionalTextAnswer
                          isIdeProjectHideBlockPalette
                          contentSectionGroup {
                            ${ContentSectionGroup}
                          }
                        }
                        ... on QuizQuestion {
                          header
                          isOptionalQuiz
                          answers {
                            ... on QuizAnswer {
                              name
                              answerTitle
                              answerBody
                              hint
                              isCorrect
                            }
                          }
                        }
                      }
                    }
                    contentSections {
                      id
                      type
                      isIdeProjectExpandedCurriculum
                      ... on ContentSection {
                        heading
                        subheading
                        content
                        facilitatorNote
                        visibleInPageNavigation
                        onlyVisibleToFacilitators
                        isOpenTextAnswer
                        requiredGrading
                        isOptionalTextAnswer
                        isIdeProjectHideBlockPalette
                        contentSectionGroup {
                          ${ContentSectionGroup}
                        }
                      }
                      ... on QuizQuestion {
                        header
                        isOptionalQuiz
                        answers {
                          ... on QuizAnswer {
                            name
                            answerTitle
                            answerBody
                            hint
                            isCorrect
                          }
                        }
                      }
                    }
                    setType {
                      name
                      colorTheme
                    }
                    icon {
                      name
                      src
                      alt
                    }
                  
`

export const getSet = ({ activitySetId, activitySetSlug }, permissionContext) => {
  const isFacilitator = permissionContext.roleId === Roles.Facilitator;

  return client
    .query({
      fetchPolicy: "no-cache",
      query: gql`
        query ActivitySet(
          $activitySetId: String
          $activitySetSlug: String
          $isFacilitator: Boolean!
        ) {
          ActivitySetQuery(
            activitySetId: $activitySetId,
            activitySetSlug: $activitySetSlug,
            permissionContext: {
              isFacilitator: $isFacilitator,
              totalProgramsCount: 1,
              filterOptional: false
            }
            ) {
                ${activitySetItem}
            }
        }
      `,
      variables: {
        activitySetId: activitySetId ?? null,
        activitySetSlug: activitySetSlug ?? null,
        isFacilitator,
      },
    });
}

const welcomeModalFields = `
  id
  name
  modalContentSections {
    name
    heading
    content
    buttonLabel
  }
`;

const imageFields = `
  name
  src
  alt
`;

/**
 * The flat curriculum shape. Replaces `CurriculumQuery` plus the client-side
 * conversion; `modules/curriculum/tree` rebuilds the map the app expects.
 */
export const getCurriculumShape = (programId, partnershipId, studentView) => {
  const id = programId || "";
  const cpId = partnershipId || "";

  return client
    .query({
      fetchPolicy: "no-cache",
      query: gql`
        {
          CurriculumShapeQuery(
            programId: "${id}",
            partnershipId: "${cpId}",
            studentView: ${studentView},
            ) {
            code
            role
            welcomeModal {
              ${welcomeModalFields}
            }
            nodes {
              path
              parentPath
              childPaths
              entityId
              entityType
              slug
              name
              order
              sectionIds
              hasContentSections
              isOptional
              endsInCertificate
              certificateCode
              certificateTypeName
              shortDescription
              externalUrl
              icon {
                ${imageFields}
              }
              coverImage {
                ${imageFields}
              }
              isBlockBasedIdeProject
              ideProjectType
              ideType
              ideProjectStarterCode {
                id
                title
                projectDescription
              }
              estimatedTimeInMinutes
              setTypeName
              setTypeColorTheme
              collectionTypeName
              collectionTypeColorTheme
              activityTypeName
              activityTypeColorTheme
              folderTypeName
              folderTypeColorTheme
              introduction
              goals
              easeOfFacilitation
              tags
              permissionLevel
              program
              invisibleCollection
              welcomeModal {
                ${welcomeModalFields}
              }
            }
            sections {
              id
              typename
              nodePaths
              groupId
              heading
              subheading
              name
              content
              facilitatorNote
              visibleInPageNavigation
              onlyVisibleToFacilitators
              isOpenTextAnswer
              requiredGrading
              isOptionalTextAnswer
              isIdeProjectHideBlockPalette
              header
              description
              isOptionalQuiz
              answers {
                name
                answerTitle
                answerBody
                hint
                isCorrect
              }
            }
            groupSections {
              groupId
              name
              requiredGrading
              sectionIds
            }
            warnings
          }
        }
      `
    });
}
